import BasicButton from "@/components/button/BasicButton"
import TextButton from "@/components/button/TextButton"

import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import "./registerStadiumDialog.scss"
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import GoogleMap from "@/app/activity/components/googleMap/GoogleMap"
import TextField from "@/components/form/textField/TextField"
import { Latlng } from "@/app/activity/components/type"
import { Stadium } from "@/types/stadium"
import { useCreateStadiumMutation } from "@/hooks/api/useStadiumQuery"
import { useActivityDataQuery } from "@/hooks/api/useActivityQuery"
import { createGlobalStore } from "@/stores/global"
import { createActivityStore } from "@/app/activity/store"

const polygonWidth = 0.001
const polygonHeight = 0.0007
const suwonWorldCupStadium = {
  latitude: 37.2864699567,
  longitude: 127.036875349,
}

export default function RegisterStadiumDialog() {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { mutateAsync: createStadiumMutation } = useCreateStadiumMutation()
  const { setSnackBar } = createGlobalStore((store) => ({
    setSnackBar: store.setSnackBar,
  }))

  const { tempActivity } = createActivityStore((state) => ({
    tempActivity: state.tempActivity,
  }))

  const { data: activityData } = useActivityDataQuery(tempActivity?.idfActivity)

  const activityAvgCenter = useMemo(() => {
    if (!activityData || activityData.length === 0) return null

    const validData = activityData.filter(
      (data) =>
        data.avgLatitude && data.avgLongitude && data.avgLatitude !== 0 && data.avgLongitude !== 0,
    )

    if (validData.length === 0) return null

    const avgLat = validData.reduce((sum, data) => sum + data.avgLatitude, 0) / validData.length
    const avgLng = validData.reduce((sum, data) => sum + data.avgLongitude, 0) / validData.length

    return { lat: avgLat, lng: avgLng }
  }, [activityData])

  const [open, setOpen] = useState(false)
  const [, setSearchValue] = useState("")
  const [searchedAddress, setSearchedAddress] = useState("")
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [stadium, setStadium] = useState<Stadium>()
  const [center, setCenter] = useState<{ lat?: number; lng?: number }>({
    lat: suwonWorldCupStadium.latitude,
    lng: suwonWorldCupStadium.longitude,
  })

  const setMapSearch = async () => {
    if (!map || !inputRef.current) {
      return
    }

    const options = { fields: ["formatted_address", "geometry"] }
    await google.maps.importLibrary("places")
    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, options)

    autocomplete.bindTo("bounds", map)
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace()
      handleSearchMap(place)
    })
  }

  const handleSearchMap = (place: google.maps.places.PlaceResult) => {
    const { geometry, formatted_address } = place

    if (!geometry) {
      return
    }

    const latitude = geometry.location?.lat() || 0
    const longitude = geometry.location?.lng() || 0
    const corners = buildCornersByPoint({ latitude, longitude })

    setSearchedAddress(formatted_address as string)

    setStadium((stadium) => ({ ...stadium, latitude, longitude, corners }))
    setCenter({ lat: latitude, lng: longitude })
  }

  const buildCornersByPoint = ({ latitude, longitude }: Latlng): Latlng[] => {
    if (!latitude || !longitude) {
      return []
    }

    return [
      { latitude: latitude - polygonHeight, longitude: longitude + polygonWidth },
      { latitude: latitude + polygonHeight, longitude: longitude + polygonWidth },
      { latitude: latitude + polygonHeight, longitude: longitude - polygonWidth },
      { latitude: latitude - polygonHeight, longitude: longitude - polygonWidth },
    ]
  }

  const onClickRegister = async () => {
    if (!stadium) {
      return
    }

    try {
      await createStadiumMutation(stadium)

      setOpen(false)
      setSnackBar({ open: true, message: t("messages.successAdd"), severity: "success" })
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (inputRef.current && map) {
      setMapSearch()
    }
  }, [inputRef.current, map])

  useEffect(() => {
    if (!open) {
      return
    }

    if (activityAvgCenter) {
      setCenter(activityAvgCenter)
    } else {
      setCenter({
        lat: suwonWorldCupStadium.latitude,
        lng: suwonWorldCupStadium.longitude,
      })
    }
  }, [open, activityAvgCenter])

  return (
    <div className="RegisterStadiumDialog">
      <TextButton variant="link" onClick={() => setOpen(true)}>
        {t("addStadium")}
      </TextButton>
      {open && (
        <Dialog
          className="register-stadium-dialog"
          open={open}
          title={t("addStadium")}
          onClose={() => setOpen(false)}>
          <DialogTitle>{t("addStadium")}</DialogTitle>
          <DialogContent className="register-stadium-dialog-content">
            <TextField
              label={t("name")}
              onChange={(e) => {
                setStadium({ ...stadium, name: e.target.value } as Stadium)
              }}
            />
            <TextField
              inputRef={inputRef}
              label={t("address")}
              value={searchedAddress}
              placeholder={t("enterAddress")}
              onChange={(e) => setSearchedAddress(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            />
            <GoogleMap
              center={center}
              setCenter={setCenter}
              setSearchValue={setSearchValue}
              setMap={setMap}
              selectedStadium={stadium}
              setSelectedStadium={setStadium}
              editable
            />
          </DialogContent>
          <DialogActions>
            <BasicButton type="default-outlined" size="md" onClick={() => setOpen(false)}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton
              type="primary-contained"
              size="md"
              onClick={onClickRegister}
              disabled={!stadium || !stadium.name || !stadium.corners?.length}>
              {t("button.save")}
            </BasicButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  )
}
